import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import VideoCallOutlinedIcon from "@mui/icons-material/VideoCallOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  AboutUsMediaType,
  createAboutUsPost,
  deleteAboutUsPost,
  getAboutUsPosts,
  reorderAboutUsPosts,
  updateAboutUsPost,
} from "../../api/aboutUsService";

// Must match AboutUsController on the API.
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_VIDEO_BYTES = 150 * 1024 * 1024;
const MAX_FILES = 10;
const MAX_CAPTION = 1000;

const KIND = {
  image: { label: "Photos", ratio: 1, ratioLabel: "1:1", aspect: "1 / 1", types: IMAGE_TYPES, maxBytes: MAX_IMAGE_BYTES },
  video: { label: "Videos", ratio: 9 / 16, ratioLabel: "9:16", aspect: "9 / 16", types: VIDEO_TYPES, maxBytes: MAX_VIDEO_BYTES },
};

const formatMb = (bytes) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

function errorText(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string" && data) return data;
  if (data?.title) return data.title;
  return fallback;
}

// Reads the real pixel size so we can warn when a file isn't the ratio the page shows.
function measure(file, kind) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    // Some formats (e.g. MOV in Chrome) never report metadata - don't block on them.
    setTimeout(() => resolve({ url, width: 0, height: 0 }), 5000);
    if (kind === "image") {
      const img = new Image();
      img.onload = () => resolve({ url, width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({ url, width: 0, height: 0 });
      img.src = url;
    } else {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => resolve({ url, width: video.videoWidth, height: video.videoHeight });
      video.onerror = () => resolve({ url, width: 0, height: 0 });
      video.src = url;
    }
  });
}

function ratioOff(item, kind) {
  if (!item.width || !item.height) return false;
  return Math.abs(item.width / item.height - KIND[kind].ratio) > 0.03;
}

function MediaFrame({ kind, src, sx }) {
  return (
    <Box
      sx={{
        position: "relative",
        aspectRatio: KIND[kind].aspect,
        overflow: "hidden",
        borderRadius: 1.5,
        bgcolor: "#0b1f19",
        ...sx,
      }}
    >
      {kind === "image" ? (
        <Box component="img" src={src} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      ) : (
        <Box
          component="video"
          src={`${src}#t=0.1`}
          muted
          playsInline
          preload="metadata"
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      )}
    </Box>
  );
}

function Composer({ onPublished }) {
  const [kind, setKind] = useState("image");
  const [items, setItems] = useState([]); // { id, file, url, width, height }
  const [caption, setCaption] = useState("");
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Release object URLs when the page unmounts.
  useEffect(() => () => itemsRef.current.forEach((i) => URL.revokeObjectURL(i.url)), []);

  function clearItems() {
    items.forEach((i) => URL.revokeObjectURL(i.url));
    setItems([]);
  }

  function switchKind(next) {
    if (!next || next === kind) return;
    if (items.length && !window.confirm("Switching type removes the files you selected. Continue?")) return;
    clearItems();
    setKind(next);
    setError("");
  }

  async function addFiles(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const spec = KIND[kind];
    const problems = [];
    const accepted = [];
    for (const file of files) {
      if (!spec.types.includes(file.type)) problems.push(`${file.name}: not a supported ${kind === "image" ? "image (JPEG, PNG, WEBP)" : "video (MP4, MOV, WEBM)"}.`);
      else if (file.size > spec.maxBytes) problems.push(`${file.name}: ${formatMb(file.size)} is over the ${formatMb(spec.maxBytes)} limit.`);
      else accepted.push(file);
    }
    const room = MAX_FILES - items.length;
    if (accepted.length > room) problems.push(`A post can hold up to ${MAX_FILES} files - only the first ${Math.max(room, 0)} were added.`);
    const measured = await Promise.all(
      accepted.slice(0, Math.max(room, 0)).map(async (file) => ({
        id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`,
        file,
        ...(await measure(file, kind)),
      })),
    );
    setItems((prev) => [...prev, ...measured]);
    setError(problems.join(" "));
  }

  function removeItem(id) {
    setItems((prev) => {
      const item = prev.find((i) => i.id === id);
      if (item) URL.revokeObjectURL(item.url);
      return prev.filter((i) => i.id !== id);
    });
  }

  function moveItem(index, delta) {
    setItems((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  async function publish() {
    if (!items.length) return;
    setError("");
    setProgress(0);
    try {
      await createAboutUsPost(
        items.map((i) => i.file),
        caption.trim(),
        (p) => setProgress(p),
      );
      clearItems();
      setCaption("");
      onPublished();
    } catch (err) {
      setError(errorText(err, "Upload failed. Please try again."));
    } finally {
      setProgress(null);
    }
  }

  const spec = KIND[kind];
  const uploading = progress !== null;
  const offCount = items.filter((i) => ratioOff(i, kind)).length;
  const totalBytes = items.reduce((sum, i) => sum + i.file.size, 0);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 3 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">New post</Typography>
          <Typography variant="body2" color="text.secondary">
            Pick one or more files. Several files in one post are shown together as a swipeable carousel, like Instagram.
          </Typography>
        </Box>
        <ToggleButtonGroup exclusive size="small" value={kind} onChange={(e, v) => switchKind(v)} disabled={uploading}>
          <ToggleButton value="image">
            <ImageOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} /> Photos · 1:1
          </ToggleButton>
          <ToggleButton value="video">
            <VideocamOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} /> Videos · 9:16
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (!uploading) addFiles(e.dataTransfer.files);
        }}
        sx={{
          p: 2,
          borderRadius: 2,
          border: "2px dashed",
          borderColor: dragOver ? "primary.main" : "divider",
          bgcolor: dragOver ? "action.hover" : "transparent",
          transition: "all .2s",
        }}
      >
        {items.length === 0 ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 4, textAlign: "center" }}>
            {kind === "image" ? <AddPhotoAlternateOutlinedIcon color="action" sx={{ fontSize: 44 }} /> : <VideoCallOutlinedIcon color="action" sx={{ fontSize: 44 }} />}
            <Typography variant="body1">Drag & drop {kind === "image" ? "photos" : "videos"} here</Typography>
            <Typography variant="caption" color="text.secondary">
              Best at {spec.ratioLabel} · up to {MAX_FILES} files · max {formatMb(spec.maxBytes)} each
            </Typography>
            <Button variant="contained" onClick={() => inputRef.current?.click()}>
              Choose {kind === "image" ? "photos" : "videos"}
            </Button>
          </Stack>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: kind === "image" ? "repeat(auto-fill, minmax(130px, 1fr))" : "repeat(auto-fill, minmax(110px, 1fr))",
              gap: 1.5,
            }}
          >
            {items.map((item, index) => (
              <Box key={item.id} sx={{ position: "relative" }}>
                <MediaFrame kind={kind} src={item.url} sx={{ border: "1px solid", borderColor: ratioOff(item, kind) ? "warning.main" : "divider" }} />
                <Chip
                  size="small"
                  label={index + 1}
                  sx={{ position: "absolute", top: 6, left: 6, height: 22, bgcolor: "rgba(0,0,0,.6)", color: "#fff" }}
                />
                {ratioOff(item, kind) && (
                  <Tooltip title={`This file is ${item.width}×${item.height}. It will be cropped to ${spec.ratioLabel} on the page.`}>
                    <WarningAmberIcon color="warning" sx={{ position: "absolute", top: 34, left: 6, bgcolor: "rgba(0,0,0,.6)", borderRadius: "50%", p: 0.3 }} />
                  </Tooltip>
                )}
                <IconButton
                  size="small"
                  onClick={() => removeItem(item.id)}
                  disabled={uploading}
                  sx={{ position: "absolute", top: 4, right: 4, bgcolor: "rgba(0,0,0,.6)", color: "#fff", "&:hover": { bgcolor: "error.main" } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Stack direction="row" justifyContent="center" sx={{ mt: 0.5 }}>
                  <IconButton size="small" onClick={() => moveItem(index, -1)} disabled={uploading || index === 0} aria-label="Move earlier">
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => moveItem(index, 1)} disabled={uploading || index === items.length - 1} aria-label="Move later">
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            ))}
            {items.length < MAX_FILES && (
              <Button
                variant="outlined"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                sx={{ aspectRatio: spec.aspect, borderStyle: "dashed", flexDirection: "column", gap: 0.5 }}
              >
                {kind === "image" ? <AddPhotoAlternateOutlinedIcon /> : <VideoCallOutlinedIcon />}
                Add more
              </Button>
            )}
          </Box>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept={spec.types.join(",")}
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </Box>

      {offCount > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {offCount} file{offCount > 1 ? "s are" : " is"} not {spec.ratioLabel}. {offCount > 1 ? "They" : "It"} will still be published, but cropped to fit
          the {spec.ratioLabel} frame.
        </Alert>
      )}

      <TextField
        label="Caption (optional)"
        value={caption}
        onChange={(e) => setCaption(e.target.value.slice(0, MAX_CAPTION))}
        multiline
        minRows={2}
        fullWidth
        disabled={uploading}
        helperText={`${caption.length}/${MAX_CAPTION}`}
        sx={{ mt: 2 }}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {uploading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress variant={progress < 1 ? "determinate" : "indeterminate"} value={progress * 100} />
          <Typography variant="caption" color="text.secondary">
            {progress < 1 ? `Uploading… ${Math.round(progress * 100)}%` : "Saving to Azure…"}
          </Typography>
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {items.length ? `${items.length} file${items.length > 1 ? "s" : ""} · ${formatMb(totalBytes)}` : ""}
        </Typography>
        <Stack direction="row" spacing={1}>
          {items.length > 0 && (
            <Button onClick={clearItems} disabled={uploading}>
              Clear
            </Button>
          )}
          <Button variant="contained" onClick={publish} disabled={!items.length || uploading}>
            {uploading ? "Publishing…" : "Publish to About Us"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function PostPreview({ post }) {
  const kind = post.mediaType === AboutUsMediaType.Video ? "video" : "image";
  const [index, setIndex] = useState(0);
  const count = post.media.length;
  const current = post.media[Math.min(index, count - 1)];
  const arrowSx = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    bgcolor: "rgba(255,255,255,.85)",
    "&:hover": { bgcolor: "#fff" },
  };

  return (
    <Box sx={{ position: "relative" }}>
      <MediaFrame key={current.aboutUsMediaId} kind={kind} src={current.mediaUrl} sx={{ borderRadius: 0 }} />
      {count > 1 && (
        <>
          <Chip
            size="small"
            icon={<CollectionsOutlinedIcon sx={{ color: "#fff !important" }} />}
            label={`${index + 1}/${count}`}
            sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(0,0,0,.6)", color: "#fff" }}
          />
          {index > 0 && (
            <IconButton size="small" onClick={() => setIndex(index - 1)} sx={{ ...arrowSx, left: 6 }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          )}
          {index < count - 1 && (
            <IconButton size="small" onClick={() => setIndex(index + 1)} sx={{ ...arrowSx, right: 6 }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          )}
        </>
      )}
    </Box>
  );
}

function EditCaptionDialog({ post, onClose, onSaved }) {
  const [caption, setCaption] = useState(post.caption || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    setSaving(true);
    setError("");
    try {
      await updateAboutUsPost(post.aboutUsPostId, caption.trim());
      onSaved();
    } catch (err) {
      setError(errorText(err, "Could not save the caption."));
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit caption</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          value={caption}
          onChange={(e) => setCaption(e.target.value.slice(0, MAX_CAPTION))}
          multiline
          minRows={3}
          fullWidth
          autoFocus
          helperText={`${caption.length}/${MAX_CAPTION}`}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AboutUsMedia() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    const data = await getAboutUsPosts();
    setPosts(Array.isArray(data) ? data : []);
  }

  useEffect(() => {
    reload()
      .catch(() => setError("Could not load About Us media. Is the API running?"))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    if (filter === "image") return posts.filter((p) => p.mediaType === AboutUsMediaType.Image);
    if (filter === "video") return posts.filter((p) => p.mediaType === AboutUsMediaType.Video);
    return posts;
  }, [posts, filter]);

  // Reordering works on the full list so hidden (filtered-out) posts keep their places.
  async function move(post, delta) {
    const same = posts.filter((p) => p.mediaType === post.mediaType);
    const i = same.findIndex((p) => p.aboutUsPostId === post.aboutUsPostId);
    const neighbor = same[i + delta];
    if (!neighbor) return;
    const ids = posts.map((p) => p.aboutUsPostId);
    const a = ids.indexOf(post.aboutUsPostId);
    const b = ids.indexOf(neighbor.aboutUsPostId);
    [ids[a], ids[b]] = [ids[b], ids[a]];
    const byId = Object.fromEntries(posts.map((p) => [p.aboutUsPostId, p]));
    setPosts(ids.map((id) => byId[id]));
    setBusy(true);
    try {
      await reorderAboutUsPosts(ids);
    } catch (err) {
      setError(errorText(err, "Could not save the new order."));
      await reload().catch(() => {});
    } finally {
      setBusy(false);
    }
  }

  async function remove(post) {
    const n = post.media.length;
    if (!window.confirm(`Delete this post${n > 1 ? ` and its ${n} files` : ""}? It is removed from the About Us page and Azure storage. This cannot be undone.`)) return;
    try {
      await deleteAboutUsPost(post.aboutUsPostId);
      await reload();
    } catch (err) {
      setError(errorText(err, "This post could not be deleted."));
    }
  }

  const photoCount = posts.filter((p) => p.mediaType === AboutUsMediaType.Image).length;
  const videoCount = posts.length - photoCount;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h4">About Us Media</Typography>
        <Button component="a" href="/about-us" target="_blank" rel="noopener" endIcon={<OpenInNewIcon />}>
          View page
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Photos (1:1) and videos (9:16) shown in the gallery on the public About Us page. Newest posts appear first; use the
        arrows to change the order.
      </Typography>

      <Composer onPublished={() => reload().catch(() => {})} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Published</Typography>
        <ToggleButtonGroup exclusive size="small" value={filter} onChange={(e, v) => v && setFilter(v)}>
          <ToggleButton value="all">All ({posts.length})</ToggleButton>
          <ToggleButton value="image">Photos ({photoCount})</ToggleButton>
          <ToggleButton value="video">Videos ({videoCount})</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {loading ? (
        <LinearProgress />
      ) : visible.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          Nothing published yet. The gallery section stays hidden on the About Us page until you add a post.
        </Typography>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 2, alignItems: "start" }}>
          {visible.map((post) => {
            const same = posts.filter((p) => p.mediaType === post.mediaType);
            const pos = same.findIndex((p) => p.aboutUsPostId === post.aboutUsPostId);
            const isVideo = post.mediaType === AboutUsMediaType.Video;
            return (
              <Paper key={post.aboutUsPostId} variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
                <PostPreview post={post} />
                <Box sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={0.75} sx={{ mb: 1 }}>
                    <Chip
                      size="small"
                      icon={isVideo ? <VideocamOutlinedIcon /> : <ImageOutlinedIcon />}
                      label={isVideo ? "Video · 9:16" : "Photo · 1:1"}
                      variant="outlined"
                    />
                    {post.media.length > 1 && <Chip size="small" label={`${post.media.length} files`} variant="outlined" />}
                  </Stack>
                  <Typography
                    variant="body2"
                    color={post.caption ? "text.primary" : "text.secondary"}
                    dir="auto"
                    sx={{ minHeight: 40, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                  >
                    {post.caption || "No caption"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(post.createdAt).toLocaleDateString()}
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                    <Box>
                      <Tooltip title="Move earlier">
                        <span>
                          <IconButton size="small" onClick={() => move(post, -1)} disabled={busy || pos === 0}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move later">
                        <span>
                          <IconButton size="small" onClick={() => move(post, 1)} disabled={busy || pos === same.length - 1}>
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    <Box>
                      <Tooltip title="Edit caption">
                        <IconButton size="small" onClick={() => setEditing(post)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete post">
                        <IconButton size="small" color="error" onClick={() => remove(post)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {editing && (
        <EditCaptionDialog
          post={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload().catch(() => {});
          }}
        />
      )}
    </Box>
  );
}
